import React, { useState, useEffect } from 'react';
import { Form, Input, Button, message, Row, Col, Select, Upload, Avatar, Space, Popconfirm, Divider } from 'antd';
import { useTranslation } from 'react-i18next';
import moment from 'moment-timezone';
import { updateUser, uploadProfileFile, deleteProfilePicture } from '../ProfileServices';
import { getBackendMessageOr } from '../../../Utils/apiMessages';
import { DeleteOutlined, EditOutlined, CameraOutlined, LockOutlined } from '@ant-design/icons';
import { useGlobal } from '../../Shared/GlobalContext/GlobalContext';
import { useErrorHandler } from '../../../Hooks/useErrorHandler';
import ImgCrop from 'antd-img-crop';
import { getFileUploadPolicy } from '../../../Utils/fileUploadPolicy';
import { LANGUAGE_OPTIONS } from '../../../Constants/language';
import { useNativeFileUpload } from '../../../Hooks/useNativeFileUpload';
import { shouldUseNativePicker } from '../../../Utils/nativeFileUpload';
import { useFileUploadPolicy } from '../../../Hooks/useFileUploadPolicy';
import { setupNativeUploadIntegration } from '../../../Utils/nativeUploadIntegration';
import ResponsiveModal from '../../Shared/ResponsiveModal/ResponsiveModal';

const languageOptions = LANGUAGE_OPTIONS;

const timezones = moment.tz.names().map((timezone) => ({
    value: timezone,
    label: timezone,
}));

// We rely on backend-provided timezone_name for display

const EditProfile = ({ handleClose, user, fetchUser }) => {
    const { t } = useTranslation();
    const [form] = Form.useForm();
    const { loading, setLoading } = useGlobal();
    const [photoUrl, setPhotoUrl] = useState(user?.picture_url || undefined);
    const [photoUploadId, setPhotoUploadId] = useState(null);
    const [deletingPhoto, setDeletingPhoto] = useState(false);
    const { policy } = useFileUploadPolicy();
    const { handleError } = useErrorHandler();
    const isNative = shouldUseNativePicker();



    // Initialize native file upload hook
    const { uploading: uploadingPhoto, handleNativeImagePick, beforeUpload } = useNativeFileUpload({
        uploadFunction: uploadProfileFile,
        policy,
        imagesOnly: true,
        onSuccess: (processedFile, uploadResponse) => {
            const url = uploadResponse?.data?.file_url;
            if (url) {
                setPhotoUrl(url);
                setPhotoUploadId(uploadResponse?.data?.id || null);
                // Use backend message_code if available, fallback to upload_success
                const successMessage = getBackendMessageOr(t, uploadResponse, 'upload_success');
                if (successMessage) {
                    message.success(successMessage);
                }
            }
        },
        // onError removed - hook will use handleError for API errors with message_code support
    });



    // Use backend-provided timezone_name for display
    const initialTimezone = user?.timezone_name || undefined;
    const initialLanguage = (user?.language || user?.locale || '') ? (user?.language || user?.locale).split('-')[0] : undefined;

    // Setup native upload integration on mobile to make ImgCrop work with native camera
    useEffect(() => {
        if (isNative) {
            const cleanup = setupNativeUploadIntegration();
            return cleanup;
        }
    }, [isNative]);

    const handleSubmit = async () => {
        try {
            setLoading(true);
            const values = await form.validateFields();

            const {
                confirmPassword,
                ...profileValues
            } = values;
            if (typeof form.getFieldsError === 'function') {
                form.setFields(
                    form.getFieldsError().map(({ name }) => ({
                        name,
                        errors: [],
                    }))
                );
            }
            const hasPasswordUpdate = Boolean(profileValues.old_password || profileValues.new_password || confirmPassword);
            let updatedValues = { ...profileValues };

            // Persist the selected timezone string as timezone_name; compute offset for compatibility
            if (values.timezone) {
                const timezone = values.timezone;
                const offset = moment.tz(timezone).utcOffset() / 60;
                updatedValues = {
                    ...profileValues,
                    timezone: offset,
                    timezone_name: timezone,
                };
            }

            if (photoUploadId) {
                updatedValues.profile_picture_file_upload_id = photoUploadId;
            }

            if (hasPasswordUpdate) {
                updatedValues.old_password = profileValues.old_password || '';
                updatedValues.new_password = profileValues.new_password || '';
            }

            // Remove unset fields to avoid sending invalid values (e.g., locale: null, password: '')
            const filteredValues = Object.fromEntries(
                Object.entries(updatedValues).filter(([, v]) => v !== null && v !== undefined && v !== '')
            );

            const payload = await updateUser(filteredValues);
            fetchUser();
            handleClose();
            // Optional: surface success notice if backend provided one
            const successText = getBackendMessageOr(t, payload, '');
            if (successText) {
                message.success(successText);
            }
        } catch (error) {
            handleError(error, form);
        } finally {
            setLoading(false);
        }
    };
    return (
        <ResponsiveModal
            title={t('edit_profile')}
            footer={
                < Button size="medium" type="primary" block loading={loading}
                    onClick={handleSubmit} >
                    {t('save_changes')}
                </Button >
            }
            open
            onCancel={handleClose}
            desktopWidth={600}
        >
            <Form
                form={form}
                layout="vertical"
                initialValues={{
                    first_name: user?.first_name,
                    last_name: user?.last_name,
                    email: user?.email,
                    gender: user?.gender,
                    language: initialLanguage,
                    timezone: initialTimezone,
                }}
            >
                <Divider orientation="left" style={{ marginTop: 0 }}>{t('personal_information')}</Divider>
                <Row style={{ marginBottom: 16 }}>
                    <Col span={24}>
                        <Space align="center" size={16}>
                            <Avatar size={64} src={photoUrl} data-file-upload-id={photoUploadId || undefined}>
                                {!photoUrl && `${user?.first_name?.[0] || ''}${user?.last_name?.[0] || ''}`}
                            </Avatar>


                            {/* Both web and native: Use ImgCrop for consistent cropping experience */}
                            <ImgCrop
                                rotationSlider
                                zoomSlider
                                aspect={1}
                                quality={0.9}
                                modalTitle={t('edit_photo')}
                                shape="round"
                            >
                                <Upload
                                    accept="image/*"
                                    showUploadList={false}
                                    beforeUpload={beforeUpload}
                                >
                                    <Button
                                        shape="circle"
                                        icon={isNative ? <CameraOutlined /> : <EditOutlined />}
                                        loading={uploadingPhoto}
                                        aria-label={t('change_photo')}
                                    />
                                </Upload>
                            </ImgCrop>

                            {photoUrl && (
                                <Popconfirm
                                    title={t('delete_profile_picture')}
                                    onConfirm={async () => {
                                        try {
                                            setDeletingPhoto(true);
                                            const payload = await deleteProfilePicture();
                                            setPhotoUrl(undefined);
                                            setPhotoUploadId(null);
                                            message.success(getBackendMessageOr(t, payload, 'profile_picture_deleted_successfully'));
                                            if (typeof fetchUser === 'function') {
                                                await fetchUser();
                                            }
                                        } catch (e) {
                                            message.error(t('failed_to_delete_profile_picture'));
                                        } finally {
                                            setDeletingPhoto(false);
                                        }
                                    }}
                                    okText={t('yes')}
                                    cancelText={t('no')}
                                >
                                    <Button danger shape="circle" loading={deletingPhoto} icon={<DeleteOutlined />} aria-label={t('delete_profile_picture')} />
                                </Popconfirm>
                            )}
                        </Space>
                    </Col>
                </Row>
                <Row gutter={[24, 0]} style={{ margin: 0 }}>
                    <Col xs={24} md={12}>
                        <Form.Item
                            name="first_name"
                            label={t('first_name')}
                        >
                            <Input size="medium" />
                        </Form.Item>
                    </Col>

                    <Col xs={24} md={12}>
                        <Form.Item
                            name="last_name"
                            label={t('last_name')}
                        >
                            <Input size="medium" />
                        </Form.Item>
                    </Col>

                    <Col md={12} xs={24}>
                        <Form.Item
                            name="email"
                            label={t('email_address')}
                            rules={[
                                { type: 'email', message: t('please_enter_a_valid_email') }
                            ]}
                        >
                            <Input size="medium" />
                        </Form.Item>
                    </Col>

                    <Col md={12} xs={24}>
                        <Form.Item
                            name="gender"
                            label={t('gender')}
                        >
                            <Select
                                options={[
                                    { value: 'male', label: t('male') },
                                    { value: 'female', label: t('female') }
                                ]}
                                showSearch={false}
                                size="medium"
                            />
                        </Form.Item>
                    </Col>

                    <Col md={12} xs={24}>
                        <Form.Item
                            name="language"
                            label={t('language')}
                            style={{ width: '100%' }}
                        >
                            <Select
                                options={languageOptions}
                                showSearch={false}
                                size="medium"
                            />
                        </Form.Item>
                    </Col>

                    <Col md={12} xs={24}>
                        <Form.Item
                            name="timezone"
                            label={t('timezone')}
                            style={{ width: '100%' }}
                        >
                            <Select
                                options={timezones}
                                showSearch
                                filterOption={(input, option) =>
                                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                }
                                size="medium"
                            />
                        </Form.Item>
                    </Col>
                </Row>
                <Divider orientation="left">{t('change_password')}</Divider>
                <Row gutter={[24, 0]} style={{ margin: 0 }}>
                    <Col xs={24} md={12}>
                        <Form.Item
                            name="old_password"
                            label={t('current_password')}
                        >
                            <Input.Password
                                prefix={<LockOutlined />}
                                placeholder={t('enter_your_current_password')}
                            />
                        </Form.Item>
                    </Col>

                    <Col xs={24} md={12}>
                        <Form.Item
                            name="new_password"
                            label={t('new_password')}
                        >
                            <Input.Password
                                prefix={<LockOutlined />}
                                placeholder={t('enter_your_new_password')}
                            />
                        </Form.Item>
                    </Col>

                    <Col xs={24} md={12}>
                        <Form.Item
                            name="confirmPassword"
                            label={t('confirm_new_password')}
                            dependencies={['new_password']}
                            rules={[
                                ({ getFieldValue }) => ({
                                    validator(_, value) {
                                        if (!value || getFieldValue('new_password') === value) {
                                            return Promise.resolve();
                                        }
                                        return Promise.reject(
                                            new Error(t('the_two_passwords_do_not_match'))
                                        );
                                    },
                                }),
                            ]}
                        >
                            <Input.Password
                                prefix={<LockOutlined />}
                                placeholder={t('confirm_your_new_password')}
                            />
                        </Form.Item>
                    </Col>
                </Row>
            </Form>
        </ResponsiveModal >
    );
};

export default EditProfile;
